import type { BattleSnapshot, GoalInfo } from '@boss/shared'
import type { Transport } from '../state/useBattle'
import { useState } from 'react'
import { apiUrl } from '../api'

const SECRET = (import.meta.env.VITE_ADMIN_SECRET as string | undefined) ?? 'dev'

async function admin(path: string, body: unknown) {
  const res = await fetch(apiUrl(`/admin/${path}`), {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-admin-secret': SECRET },
    body: JSON.stringify(body),
  })
  if (!res.ok) console.warn(`admin/${path} -> ${res.status}`, await res.text())
}

/** What this browser shows instead of the live state. Nothing here reaches the server. */
export interface LocalOverride {
  /** Progress 0..1 applied to the boss and to every goal. */
  progress?: number
  defeated?: boolean
  window?: { startsAt: string | null; endsAt: string | null }
}

export interface LocalActions {
  set(patch: Partial<LocalOverride>): void
  clear(): void
  defeat(): void
  hit(kind: 'plain' | 'super' | 'crit' | 'both'): void
  sampleFeed(): void
}

interface Props {
  transport: Transport
  error: string | null
  goals: GoalInfo[]
  snapshot: BattleSnapshot | null
  override: LocalOverride
  local: LocalActions
}

const STAGE_PROGRESS = [0.1, 0.35, 0.6, 0.85] // inside stages 1..4 (thresholds at 25/50/75 %)
const HEALTH = [
  ['100 %', 0],
  ['70 %', 0.3],
  ['40 %', 0.6],
  ['15 %', 0.85],
] as const
const hour = 3600 * 1000

export function DebugPanel({ transport, error, goals, snapshot, override, local }: Props) {
  const [open, setOpen] = useState(true)
  const active = Object.keys(override).length > 0
  const setAll = async (pct: number) => {
    for (const g of goals) await admin('set-count', { goalId: g.id, count: Math.round((g.target * pct) / 100) })
  }
  return (
    <div className={`debug${open ? '' : ' collapsed'}`}>
      <button className="debug-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        debug · {transport}
        {snapshot ? ` · ${(snapshot.progress * 100).toFixed(2)}%` : ''}
        {active ? ' · LOCAL PREVIEW' : ''}
        {error ? ` · ${error}` : ''}
      </button>
      {open && (
        <div className="debug-body">
          <p className="debug-head">Local preview (this browser only)</p>
          <div className="debug-row">
            <span className="debug-label">Stage</span>
            {STAGE_PROGRESS.map((p, i) => (
              <button key={i} onClick={() => local.set({ progress: p, defeated: false })}>
                {i + 1}
              </button>
            ))}
          </div>
          <div className="debug-row">
            <span className="debug-label">Health</span>
            {HEALTH.map(([label, p]) => (
              <button key={label} onClick={() => local.set({ progress: p, defeated: false })}>
                {label}
              </button>
            ))}
            <button onClick={() => local.defeat()}>Defeat</button>
          </div>
          <div className="debug-row">
            <span className="debug-label">Time</span>
            <button onClick={() => local.set({ window: { startsAt: new Date(Date.now() + 26 * hour).toISOString(), endsAt: new Date(Date.now() + 8 * 24 * hour).toISOString() } })}>
              Not started
            </button>
            <button onClick={() => local.set({ window: { startsAt: new Date(Date.now() - hour).toISOString(), endsAt: new Date(Date.now() + 2 * hour).toISOString() } })}>
              Last 2 h
            </button>
            <button onClick={() => local.set({ window: { startsAt: new Date(Date.now() - 7 * 24 * hour).toISOString(), endsAt: new Date(Date.now() - hour).toISOString() }, defeated: false })}>
              Ran out
            </button>
          </div>
          <div className="debug-row">
            <span className="debug-label">Hit FX</span>
            <button onClick={() => local.hit('plain')}>×1</button>
            <button onClick={() => local.hit('super')}>Super</button>
            <button onClick={() => local.hit('crit')}>Crit</button>
            <button onClick={() => local.hit('both')}>Both</button>
            <button onClick={() => local.sampleFeed()}>Feed rows</button>
          </div>
          <div className="debug-row">
            <button className="debug-clear" onClick={() => local.clear()} disabled={!active}>
              Back to live
            </button>
          </div>

          <p className="debug-head">Server (needs the admin secret, changes it for everyone)</p>
          <div className="debug-row">
            {goals.map((g) => (
              <button key={g.id} onClick={() => void admin('hit', { packCode: g.codes[0], userRef: 'debug', tier: 'common', crit: false })}>
                Open {g.label}
              </button>
            ))}
          </div>
          <div className="debug-row">
            <button onClick={() => void admin('simulate', { count: 12, intervalMs: 350 })}>Simulate 12</button>
            {[0, 26, 51, 76].map((pct) => (
              <button key={pct} onClick={() => void setAll(pct)}>
                Goals → {pct}%
              </button>
            ))}
            <button onClick={() => void admin('reset', {})}>Reset battle</button>
          </div>
        </div>
      )}
    </div>
  )
}
